/**
 * The StatusPill label map — the single source of truth for how every lifecycle in the API
 * contract reads to a person (hand-off COMPONENTS.md "StatusPill map", designs/app/StatusPill.dc.html).
 *
 * Never print a raw status value. A status this map does not know renders as a neutral pill
 * with the value turned into words ("needs_input" → "Needs input"), so a new server state is
 * visible and readable while somebody adds it here — never hidden, never an enum dump.
 */
import type { BadgeVariantKey } from '../lib/uiConstants';

export type StatusPillEntry = readonly [label: string, variant: BadgeVariantKey];

/**
 * Lifecycles the contract declares. Open vocabulary on the way in (a `string`), so a page can
 * pass the server's `status` straight through; the map decides how it reads.
 */
export const STATUS_PILL_MAP = {
  connection: {
    inferring: ['Reading API', 'syncQueued'],
    'awaiting-review': ['Needs review', 'warning'],
    approved: ['Ready', 'info'],
    active: ['Syncing', 'syncActive'],
    error: ['Problem', 'syncError'],
    archived: ['Archived', 'outline'],
  },
  pipeline: {
    draft: ['Draft', 'outline'],
    reviewing: ['In review', 'warning'],
    active: ['Live', 'syncActive'],
    paused: ['Paused', 'syncPaused'],
    error: ['Problem', 'syncError'],
    upgrading: ['Updating', 'syncQueued'],
    archived: ['Archived', 'outline'],
  },
  run: {
    queued: ['Queued', 'syncQueued'],
    running: ['Running', 'syncActive'],
    completed: ['Done', 'success'],
    failed: ['Failed', 'syncError'],
    cancelled: ['Cancelled', 'outline'],
  },
  breaker: {
    tripped: ['Stopped', 'destructive'],
    held_open: ['Kept stopped', 'warning'],
    open: ['Partly let through', 'info'],
    forced_closed: ['Resumed by hand', 'outline'],
  },
  safety: {
    closed: ['Normal', 'success'],
    open: ['Stopped', 'destructive'],
    half_open: ['Watching closely', 'info'],
    held_open: ['Kept stopped', 'warning'],
  },
  proposal: {
    pending: ['Waiting', 'warning'],
    approved: ['Approved', 'success'],
    rejected: ['Rejected', 'outline'],
    expired: ['Expired', 'outline'],
  },
  load: {
    pending: ['Needs approval', 'warning'],
    approved: ['Approved', 'info'],
    loading: ['Loading', 'syncActive'],
    paused: ['Paused', 'syncPaused'],
    completed: ['Done', 'success'],
    failed: ['Failed', 'syncError'],
    cancelled: ['Cancelled', 'outline'],
  },
  promotion: {
    pending_approval: ['Needs approval', 'warning'],
    applied: ['Applied', 'success'],
    rejected: ['Rejected', 'outline'],
    failed: ['Failed', 'syncError'],
  },
  restore: {
    preview: ['Preview', 'outline'],
    pending_approval: ['Needs approval', 'warning'],
    approved: ['Approved', 'info'],
    restoring: ['Restoring', 'syncActive'],
    verifying: ['Checking', 'syncQueued'],
    completed: ['Done', 'success'],
    failed: ['Failed', 'syncError'],
    cancelled: ['Cancelled', 'outline'],
  },
  migration: {
    pending: ['Waiting', 'warning'],
    backing_up: ['Backing up', 'syncQueued'],
    executing: ['Moving records', 'syncActive'],
    verifying: ['Checking', 'syncQueued'],
    completed: ['Done', 'success'],
    failed: ['Failed', 'syncError'],
    cancelled: ['Cancelled', 'outline'],
  },
  user: {
    active: ['Active', 'success'],
    pending_approval: ['Awaiting approval', 'warning'],
    invited: ['Invited', 'info'],
    deactivated: ['Deactivated', 'outline'],
  },
  ticket: {
    open: ['Open', 'info'],
    in_progress: ['In progress', 'syncActive'],
    resolved: ['Resolved', 'success'],
    closed: ['Closed', 'outline'],
  },
  partner: {
    surfaced: ['New', 'info'],
    viewed: ['Seen', 'outline'],
    accepted: ['Accepted', 'success'],
    dismissed: ['Dismissed', 'outline'],
  },
  suggestion: {
    pending: ['Suggested', 'info'],
    accepted: ['Accepted', 'success'],
    dismissed: ['Dismissed', 'outline'],
  },
  risk: {
    low: ['Low risk', 'success'],
    medium: ['Medium risk', 'warning'],
    high: ['High risk', 'destructive'],
  },
  severity: {
    info: ['Info', 'info'],
    warning: ['Warning', 'warning'],
    error: ['Error', 'destructive'],
    critical: ['Critical', 'destructive'],
  },
  confidence: {
    high: ['High confidence', 'confidenceHigh'],
    medium: ['Check this', 'confidenceMedium'],
    low: ['Unsure', 'confidenceLow'],
  },
  sensitivity: {
    pii: ['Personal', 'warning'],
    financial: ['Financial', 'warning'],
    health: ['Health', 'warning'],
    credential: ['Credential', 'destructive'],
    none: ['Not sensitive', 'outline'],
  },
  heal: {
    '1': ['Fixed automatically', 'healingAuto'],
    '2': ['Fixed, told you', 'healingAi'],
    '3': ['Needs your answer', 'healingHuman'],
    auto: ['Fixed automatically', 'healingAuto'],
    ai: ['Fixed, told you', 'healingAi'],
    human: ['Needs your answer', 'healingHuman'],
  },
} as const satisfies Record<string, Record<string, StatusPillEntry>>;

/** Which lifecycle a status belongs to. */
export type StatusPillOf = keyof typeof STATUS_PILL_MAP;

/** "needs_input" / "awaiting-review" → "Needs input" / "Awaiting review". */
export function humanizeStatus(status: string): string {
  const words = status.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * Confidence arrives as a score (0–100 or 0–1) as often as a band. The design's bands:
 * ≥90 high, 60–89 medium, below 60 low.
 */
export function confidenceBand(score: number): 'high' | 'medium' | 'low' {
  const pct = score <= 1 ? score * 100 : score;
  if (pct >= 90) return 'high';
  if (pct >= 60) return 'medium';
  return 'low';
}

/** The label and badge variant for a status. Unknown statuses read as words, in a neutral pill. */
export function statusPillFor(of: StatusPillOf, status: string | number): { label: string; variant: BadgeVariantKey } {
  const key = of === 'confidence' && typeof status === 'number' ? confidenceBand(status) : String(status);
  const entry = (STATUS_PILL_MAP[of] as Record<string, StatusPillEntry | undefined>)[key];
  if (entry) return { label: entry[0], variant: entry[1] };
  return { label: humanizeStatus(key), variant: 'secondary' };
}
